import { gettext } from '@eventbrite/i18n';
import React from 'react';
import {
    CASH_OUT_DAYS_LIMIT,
    MAKE_SURE_YOU_GET_PAID,
    ONE_DAY_IN_MILLIS,
    SET_UP_YOUR_ACCOUNT,
} from '../constants';
import { NotificationMessageType } from './types';

export const getLocalizedPayoutAmount = (
    locale: string,
    payoutAmount: number,
    currency: string,
) => {
    const formattedLocale = locale?.replace('_', '-');
    const localizedPayoutAmount = payoutAmount?.toLocaleString(
        formattedLocale,
        {
            style: 'currency',
            currency,
        },
    );

    return localizedPayoutAmount;
};

export const isDateOld = (dateInString: string) => {
    const today = new Date().getTime();
    const date = Date.parse(dateInString);
    return date < today;
};

export const getDaysToCashOut = (endDateString: string) => {
    const today = new Date().getTime();
    const endDate = new Date(endDateString).getTime();
    const daysToCashOuInMillis = CASH_OUT_DAYS_LIMIT * ONE_DAY_IN_MILLIS;
    const millisLeftToCashOut = endDate - today + daysToCashOuInMillis;
    const daysLeftToCashOut = Math.ceil(
        millisLeftToCashOut / ONE_DAY_IN_MILLIS,
    );

    return daysLeftToCashOut;
};

export const getNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    const localizedPayoutAmount = getLocalizedPayoutAmount(
        locale,
        payoutAmount,
        currency,
    );
    const daysToCashOut = getDaysToCashOut(endDate);
    const isEventEnded = isDateOld(endDate);

    const title = isEventEnded
        ? gettext(
              '%(daysToCashOut)s days left to receive your %(localizedPayoutAmount)s in event sales',
              { daysToCashOut, localizedPayoutAmount },
          )
        : gettext('You have %(localizedPayoutAmount)s in event sales', {
              localizedPayoutAmount,
          });
    const description = gettext(
        'To receive your funds from %(eventName)s, set up a bank account',
        { eventName },
    );
    return { title, description };
};

export const getLocalizedDate = (
    date: string,
    locale = 'en-US',
    options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    },
) => {
    const formattedLocale = locale?.replace('_', '-');
    const localizedDate = new Date(date).toLocaleDateString(
        formattedLocale,
        options,
    );
    return localizedDate;
};

export const getPayoutMethodRequestEventEndedModalNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    const localizedPayoutAmount = getLocalizedPayoutAmount(
        locale,
        payoutAmount,
        currency,
    );
    const formattedEndDate = getLocalizedDate(endDate, locale);
    const description = (
        <>
            <p>
                {gettext(
                    'To receive your %(localizedPayoutAmount)s payout from your event that ended on %(formattedEndDate)s, add a bank account.',
                    {
                        localizedPayoutAmount,
                        formattedEndDate,
                    },
                )}
            </p>
            <p>
                {gettext(
                    "We can't deliver your funds until you add a bank account.",
                )}
            </p>
        </>
    );
    if (payoutAmount > 0) {
        const { title } = getNotificationMessage({
            currency,
            endDate,
            eventName,
            locale,
            payoutAmount,
        });
        return { title, description };
    }

    const title = MAKE_SURE_YOU_GET_PAID;

    return { title, description };
};

export const getFinancialMissingInformationModalNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    if (payoutAmount > 0) {
        const { title, description } = getNotificationMessage({
            currency,
            endDate,
            eventName,
            locale,
            payoutAmount,
        });
        return { title, description };
    }

    const title = MAKE_SURE_YOU_GET_PAID;
    const description = SET_UP_YOUR_ACCOUNT;

    return { title, description };
};
